import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getCart, removeFromCart, sendToWhatsApp } from '../../services/CartService';

import '../static/ShoppingCart.css';
import '../static/Global.css';

function ShoppingCart() {
    const { uuid } = useParams(); 
    const [items, setItems] = useState([]);
    const [isSavedOrder, setIsSavedOrder] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (uuid) {
            fetch(`/api/pedidos?uuid=${uuid}`)
                .then(res => res.json())
                .then(data => {
                    setItems(data.items || []);
                    setIsSavedOrder(true);
                    setLoading(false);
                })
                .catch(() => setLoading(false));
        } else {
            // Cargamos desde el servicio de carrito
            setItems(getCart());
            setIsSavedOrder(false);
            setLoading(false);
        }
    }, [uuid]);

    // --- FUNCIÓN DE ACTUALIZACIÓN AUTOMÁTICA ---
    const handleUpdateQuantity = (productId, delta) => {
        // 1. Calculamos el nuevo estado basado en el anterior (prevItems)
        // Esto garantiza que siempre usemos la data más reciente
        setItems(prevItems => {
            const updatedCart = prevItems.map(item => {
                if (item.productos_id === productId) {
                    const nuevaCantidad = item.cantidad + delta;
                    return { ...item, cantidad: nuevaCantidad > 0 ? nuevaCantidad : 1 };
                }
                return item;
            });

            // 2. Sincronizamos con LocalStorage inmediatamente
            localStorage.setItem('mediexpress_cart', JSON.stringify(updatedCart));
            
            // 3. Disparamos el evento para el Header/Contador
            window.dispatchEvent(new Event('cartUpdated'));

            return updatedCart;
        });
    };

    const handleDeleteItem = (productId) => {
        const newCart = removeFromCart(productId);
        setItems(newCart);
        window.dispatchEvent(new Event('cartUpdated'));
    };

    const handleFinalizeOrder = async () => {
        if (items.length === 0) return;
        try {
            setLoading(true);
            await sendToWhatsApp(items);
            setItems([]); 
            localStorage.removeItem('mediexpress_cart'); // Limpiamos al finalizar
            window.dispatchEvent(new Event('cartUpdated'));
        } catch (error) {
            console.error("Error:", error);
            alert("Hubo un problema al conectar con WhatsApp.");
        } finally {
            setLoading(false);
        }
    };

    // Cálculo del total (se recalcula automáticamente cuando 'items' cambia)
const total = items.reduce((acc, item) => {
    // Intentamos obtener el precio de cualquier variante posible
    const precio = item.precio || item.precio_venta || 0; 
    return acc + (Number(precio) * item.cantidad);
}, 0);

    if (loading) return <div className="text-center py-5 text-primary"><i className="fas fa-spinner fa-spin me-2"></i>Cargando Carrito...</div>;

    return (
        <main className="main__section animate__animated animate__fadeIn">
            <h1 className="main__title text-center mt-4 fw-bold">Mi Carrito</h1>
            <article className="container py-5">
                <div className="row">
                    <div className="col-lg-8">
                        {items.length === 0 ? (
                            <div className="alert alert-info border-0 shadow-sm text-center py-4">
                                <i className="fa-solid fa-cart-shopping mb-2 fs-1"></i>
                                <p className="mb-0">Tu carrito está vacío.</p>
                            </div>
                        ) : (
                            items.map((item) => (
                                <div key={item.productos_id} className="card mb-3 p-3 shadow-sm border-0 cart-item-card hover-shadow">
                                    <div className="row align-items-center">
                                        <div className="col-3 col-md-2 text-center">
                                            <img src={item.imagen} className="img-fluid rounded shadow-sm" alt={item.nombre} style={{maxHeight: '80px'}} />
                                        </div>
                                        <div className="col-9 col-md-4">
                                            <h5 className="fw-bold mb-0">{item.nombre}</h5>
                                            <p className="text-primary fw-bold mb-0">S/ {Number(item.precio || item.precio_venta || 0).toFixed(2)}</p>
                                        </div>
                                        <div className="col-12 col-md-6 mt-3 mt-md-0 d-flex justify-content-between align-items-center">
                                            {!isSavedOrder ? (
                                                <>
                                                    <div className="quantity-control d-flex align-items-center bg-light rounded-pill p-1 border">
                                                        <button 
                                                            onClick={() => handleUpdateQuantity(item.productos_id, -1)}
                                                            className="btn btn-qty rounded-circle shadow-sm"
                                                            disabled={item.cantidad <= 1}
                                                        >
                                                            <i className="fa-solid fa-minus"></i>
                                                        </button>
                                                        <span className="px-3 fw-bold fs-5" style={{minWidth: '40px', textAlign: 'center'}}>{item.cantidad}</span>
                                                        <button 
                                                            onClick={() => handleUpdateQuantity(item.productos_id, 1)} 
                                                            className="btn btn-qty rounded-circle shadow-sm"
                                                        >
                                                            <i className="fa-solid fa-plus"></i>
                                                        </button>
                                                    </div>
                                                    <button onClick={() => handleDeleteItem(item.productos_id)} className="btn btn-delete text-danger ms-2 p-2">
                                                        <i className="fa-solid fa-trash-can fs-5"></i>
                                                    </button>
                                                </>
                                            ) : (
                                                <span className="ms-auto badge rounded-pill bg-dark px-3 py-2">Cantidad: {item.cantidad}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    <div className="col-lg-4">
                        <div className="card p-4 shadow border-0 summary-card sticky-top" style={{top: '20px'}}>
                            <h4 className="fw-bold mb-4">Resumen de Compra</h4>
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Subtotal:</span>
                                <span className="fw-bold">S/ {total.toFixed(2)}</span>
                            </div>
                            <hr />
                            <div className="d-flex justify-content-between mb-4">
                                <span className="fw-bold">Total:</span>
                                <span className="fw-bold fs-2 text-success">S/ {total.toFixed(2)}</span>
                            </div>

                            {!isSavedOrder ? (
                                <button 
                                    onClick={handleFinalizeOrder} 
                                    className="btn btn-whatsapp-order w-100 rounded-pill py-3 fw-bold shadow-lg"
                                    disabled={items.length === 0 || loading}
                                >
                                    {loading ? 'PROCESANDO...' : 'PEDIR POR WHATSAPP'} <i className="fa-brands fa-whatsapp ms-2"></i>
                                </button>
                            ) : (
                                <div className="alert alert-success border-0 text-center shadow-sm">
                                    <i className="fa-solid fa-check-circle me-2"></i> Pedido registrado con éxito
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </article>
        </main>
    );
}

export default ShoppingCart;